
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRetry } from './common/invokeWithRetry';

export interface SettingsResponse {
  key: string;
  value: Record<string, any>;
  gym_id: string;
  app_name?: string;
}

export interface BrandAssetUpload {
  slot: 'dashboard' | 'schedule' | 'reservations' | 'stats' | 'subscriptions' | 'profile' | 'loginLight' | 'loginDark' | 'logo-light' | 'logo-dark';
  fileName: string;
  contentType: 'image/png' | 'image/jpeg' | 'image/webp';
  base64: string;
}

/**
 * Fetch settings for the current gym
 */
export async function getSettings(gymId: string, key: string = 'app_config'): Promise<SettingsResponse | null> {
  const result = await invokeWithRetry<{ setting: SettingsResponse | null }>(
    'settings-management',
    { action: 'get_settings', gym_id: gymId, key }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error fetching settings:', result.error);
    return null;
  }

  return result.data?.setting || null;
}

/**
 * Update or create settings for the current gym
 */
export async function updateSettings(
  gymId: string, 
  key: string, 
  value: Record<string, any>
): Promise<SettingsResponse | null> {
  const result = await invokeWithRetry<{ setting: SettingsResponse }>(
    'settings-management',
    { action: 'update_settings', gym_id: gymId, key, value }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error updating settings:', result.error);
    throw new Error(result.error?.message || 'Failed to update settings');
  }

  return result.data?.setting || null;
}

export async function uploadBrandAsset(gymId: string, upload: BrandAssetUpload): Promise<string> {
  const result = await invokeWithRetry<{ url: string }>('settings-management', {
    action: 'upload_brand_asset', gym_id: gymId, slot: upload.slot,
    file_name: upload.fileName, content_type: upload.contentType, base64: upload.base64,
  });
  if (result.authExpired || result.error || !result.data?.url) {
    throw new Error(result.error?.message || 'Failed to upload brand asset');
  }
  return result.data.url;
}

/**
 * Get all settings for the current gym
 */
export async function getAllSettings(gymId: string): Promise<SettingsResponse[]> {
  const result = await invokeWithRetry<{ settings: SettingsResponse[] }>(
    'settings-management',
    { action: 'get_all_settings', gym_id: gymId }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error fetching all settings:', result.error);
    return [];
  }

  return result.data?.settings || [];
}

/**
 * Get app name for the current gym
 */
export async function getAppName(gymId: string): Promise<string | null> {
  const result = await invokeWithRetry<{ app_name: string | null }>(
    'settings-management',
    { action: 'get_app_name', gym_id: gymId }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error fetching app name:', result.error);
    return null;
  }

  return result.data?.app_name || null;
}

export interface Gym {
  id: string;
  name: string;
}

/**
 * Get all gyms (for admin views)
 */
export async function getGyms(gymId: string): Promise<Gym[]> {
  const result = await invokeWithRetry<{ gyms: Gym[] }>(
    'settings-management',
    { action: 'get_gyms', gym_id: gymId }
  );

  if (result.authExpired || result.error) {
    console.error('❌ Error fetching gyms:', result.error);
    return [];
  }

  return result.data?.gyms || [];
}
